// Расчёты цефалометрического анализа по Петровику. Чистые функции без React —
// их можно тестировать отдельно (см. cephalometry.test.js).

export const POINT_KEYS = ['S', 'N', 'A', 'B', 'SpNa', 'SpNз', 'Me', 'Go']; // 'з' — кириллица, ключ в файлах переводов
export const TOTAL_POINTS = 8;

/**
 * Дерево решений: ветка T1 -> ветка T2 -> условие по T3 -> код прогноза.
 * Условия записаны строками: "T3<=1.5", "1.5<T3<=5.5".
 */
export const TREE = {
  'T1>6': {
    'T2>3': { 'T3<=1.5': 'A3 MOB', '1.5<T3<=5.5': 'A1 NOB', '5.5<T3<=8.5': 'A1 DOB', 'T3>8.5': 'A2 DOB' },
    '0<=T2<=3': { 'T3<=0': 'A3 MN', '0<T3<=4': 'A1 NN', '4<T3<=7': 'A1 DN', 'T3>7': 'A2 DN' },
    'T2<0': { 'T3<=-1.5': 'A3 MDB', '-1.5<T3<=3': 'A1 NDB', '3<T3<=6': 'A1 DDB', 'T3>6': 'A2 DDB' },
  },
  '0<=T1<=6': {
    // Раньше здесь было "5>T3" (то есть T3<5): значения T3>5 не получали прогноза.
    'T2>3': { 'T3<=1': 'R3 MOB', '1<T3<=5': 'R1 NOB', 'T3>5': 'R2 DOB' },
    '0<=T2<=3': { 'T3<=0': 'R3 MN', '0<T3<=4': 'R1 NN', 'T3>4': 'R2 DN' },
    'T2<0': { 'T3<=-1': 'R3 MDB', '-1<T3<=3': 'R1 NDB', 'T3>3': 'R2 DDB' },
  },
  'T1<0': {
    'T2>3': { 'T3<-6': 'P3 MOB', '-6<=T3<1': 'P1 MOB', '1<=T3<5.5': 'P1 NOB', 'T3>=5.5': 'P2 DOB' },
    '0<=T2<=3': { 'T3<-7': 'P3 MN', '-7<=T3<0': 'P1 MN', '0<=T3<4': 'P1 NN', 'T3>=4': 'P2 DN' },
    'T2<0': { 'T3<-8': 'P3 MD', '-8<=T3<-1': 'P1 MDB', '-1<=T3<3': 'P1 NDB', 'T3>=3': 'P2 DDB' },
  },
};

const FLIP = { '<': '>', '>': '<', '<=': '>=', '>=': '<=' };
const NUM = '[\\d.\\-]+';
const VAR = 'T[123]';
const RANGE_RE = new RegExp(`^(${NUM})\\s*([<>]=?)\\s*(${VAR})\\s*([<>]=?)\\s*(${NUM})$`);
const SINGLE_RE = new RegExp(`^(${VAR})\\s*([<>]=?)\\s*(${NUM})$`);
const SINGLE_REV_RE = new RegExp(`^(${NUM})\\s*([<>]=?)\\s*(${VAR})$`);

export function parseCond(cond) {
  let m = cond.match(RANGE_RE);
  if (m) return { type: 'range', lo: parseFloat(m[1]), op1: m[2], var: m[3], op2: m[4], hi: parseFloat(m[5]) };
  m = cond.match(SINGLE_RE);
  if (m) return { type: 'single', var: m[1], op: m[2], val: parseFloat(m[3]) };
  m = cond.match(SINGLE_REV_RE);
  if (m) return { type: 'single', var: m[3], op: FLIP[m[2]], val: parseFloat(m[1]) };
  return null;
}

function checkSingle(op, val, actual) {
  switch (op) {
    case '<': return actual < val;
    case '<=': return actual <= val;
    case '>': return actual > val;
    case '>=': return actual >= val;
    default: return false;
  }
}

export function checkCond(parsed, t1, t2, t3) {
  const v = { T1: t1, T2: t2, T3: t3 }[parsed.var];
  if (parsed.type === 'single') return checkSingle(parsed.op, parsed.val, v);
  return checkSingle(FLIP[parsed.op1], parsed.lo, v) && checkSingle(parsed.op2, parsed.hi, v);
}

// Условия разбираются один раз, а не при каждом расчёте.
const PARSED_TREE = Object.fromEntries(Object.entries(TREE).map(([b1, n1]) => [b1, Object.fromEntries(
  Object.entries(n1).map(([b2, n2]) => [b2, Object.entries(n2).map(([cond, result]) => [parseCond(cond), result])]),
)]));

export function predict(t1, t2, t3) {
  if (![t1, t2, t3].every(Number.isFinite)) return null;
  const b1 = t1 > 6 ? 'T1>6' : t1 >= 0 ? '0<=T1<=6' : 'T1<0';
  const b2 = t2 > 3 ? 'T2>3' : t2 >= 0 ? '0<=T2<=3' : 'T2<0';
  for (const [parsed, result] of PARSED_TREE[b1][b2]) {
    if (parsed && checkCond(parsed, t1, t2, t3)) return result;
  }
  return null;
}

/** "A1 NOB" -> "A1N": ключ текста прогноза в файлах переводов. */
export const extractPredictionCode = (prediction) => {
  if (!prediction) return null;
  const parts = prediction.trim().split(/\s+/);
  return parts.length >= 2 ? parts[0] + parts[1][0] : prediction;
};

const RAD = 180 / Math.PI;

/** Угол в вершине vertex между лучами к p1 и p2, градусы. */
export function calcAngle(p1, vertex, p2) {
  const v1 = { x: p1.x - vertex.x, y: p1.y - vertex.y };
  const v2 = { x: p2.x - vertex.x, y: p2.y - vertex.y };
  const norm = Math.hypot(v1.x, v1.y) * Math.hypot(v2.x, v2.y);
  if (norm === 0) return 0;
  return Math.acos(Math.max(-1, Math.min(1, (v1.x * v2.x + v1.y * v2.y) / norm))) * RAD;
}

const lineAngle = (p1, p2) => Math.atan2(p2.y - p1.y, p2.x - p1.x) * RAD;

/** Острый угол между прямыми p1p2 и p3p4. */
export function angleBetweenLines(p1, p2, p3, p4) {
  // разница направлений может достигать 360°, поэтому приводим к [0, 180)
  const diff = Math.abs(lineAngle(p1, p2) - lineAngle(p3, p4)) % 180;
  return diff > 90 ? 180 - diff : diff;
}

/**
 * Результаты по расставленным точкам (порядок: S, N, A, B, SpNa, SpNz, Me, Go).
 * До 4 точек расчёта нет; индексы T1–T3 и прогноз появляются после 8 точек.
 */
export function computeResults(points) {
  if (!points || points.length < 4) return null;
  const [S, N, A, B] = points;
  const sna = calcAngle(S, N, A);
  const snb = calcAngle(S, N, B);
  const anb = sna - snb;
  const out = {
    sna, snb, anb,
    mlSnlReal: null, nlNslReal: null, mlSnlCalc: null, nlNslCalc: null,
    t1: null, t2: null, t3: null, prediction: null, predictionCode: null,
  };
  if (points.length >= TOTAL_POINTS) {
    const [, , , , SpNa, SpNz, Me, Go] = points;
    out.mlSnlReal = angleBetweenLines(Me, Go, S, N);
    out.nlNslReal = angleBetweenLines(SpNa, SpNz, S, N);
    out.mlSnlCalc = 192 - 2 * snb;
    out.nlNslCalc = out.mlSnlReal / 2 - 7;
    out.t1 = out.mlSnlCalc - out.mlSnlReal;
    out.t2 = out.nlNslCalc - out.nlNslReal;
    out.t3 = anb;
    out.prediction = predict(out.t1, out.t2, out.t3);
    out.predictionCode = extractPredictionCode(out.prediction);
  }
  return out;
}
